import styled from '@emotion/styled';
import SendIcon from '@mui/icons-material/Send';
import { Box, IconButton, Typography } from '@mui/material';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import {
  colorAgentBorder,
  colorAgentInputBorder,
  colorAgentInputFocusBorder,
  colorAgentTextDisabled,
  colorAgentTextPrimary,
  colorNeutralGray,
  colorNeutralWhite,
  colorPrimaryDeepBlue,
  colorSemanticError,
  fontFamilyJa,
  fontSizeSm,
  fontSizeXs,
  lineHeightNormal,
  radiusSm,
  space1,
  space3,
} from '../styles/tokens';

const MAX_LENGTH = 2000;

const Wrapper = styled(Box)`
  border-top: 1px solid ${colorAgentBorder};
`;

const InputContainer = styled(Box)`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 10px ${space3} ${space1};
`;

const StyledTextArea = styled.textarea`
  flex: 1;
  resize: none;
  border: 1px solid ${colorAgentInputBorder};
  border-radius: ${radiusSm};
  padding: 7px 10px;
  font-size: ${fontSizeSm};
  font-family: ${fontFamilyJa};
  line-height: ${lineHeightNormal};
  min-height: 36px;
  max-height: 100px;
  outline: none;
  color: ${colorAgentTextPrimary};

  &:focus {
    border-color: ${colorAgentInputFocusBorder};
  }

  &:disabled {
    background-color: ${colorNeutralGray};
    color: ${colorAgentTextDisabled};
  }
`;

const SendButton = styled(IconButton)`
  background-color: ${colorPrimaryDeepBlue};
  color: ${colorNeutralWhite};
  width: 30px;
  height: 30px;
  border-radius: ${radiusSm};

  &:hover {
    background-color: ${colorPrimaryDeepBlue};
    opacity: 0.9;
  }

  &:disabled {
    background-color: ${colorAgentBorder};
    color: ${colorAgentTextDisabled};
  }
`;

const CharCount = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isOver',
})<{ isOver: boolean }>`
  font-size: ${fontSizeXs};
  color: ${({ isOver }) => (isOver ? colorSemanticError : colorAgentTextDisabled)};
  text-align: right;
  padding: 0 ${space3};
`;

const Footer = styled(Typography)`
  font-size: 10px;
  color: ${colorAgentTextDisabled};
  text-align: center;
  padding: 2px ${space3} 6px;
`;

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

export function ChatInput({ onSend, disabled = false }: ChatInputProps) {
  const [value, setValue] = useState('');
  const isOver = value.length > MAX_LENGTH;
  const canSend = value.trim().length > 0 && !isOver && !disabled;

  const handleSend = () => {
    if (!canSend) return;
    onSend(value.trim());
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Wrapper>
      {value.length > MAX_LENGTH - 200 && (
        <CharCount isOver={isOver}>
          {value.length} / {MAX_LENGTH}
        </CharCount>
      )}
      <InputContainer>
        <StyledTextArea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="質問を入力してください..."
          disabled={disabled}
          rows={1}
        />
        <SendButton onClick={handleSend} disabled={!canSend} size="small">
          <SendIcon sx={{ fontSize: 16 }} />
        </SendButton>
      </InputContainer>
      <Footer>
        AIアシスタントの回答は必ずしも正しいとは限りません。重要な情報は確認するようにしてください。
      </Footer>
    </Wrapper>
  );
}
